/**
 * Hiển thị từng đơn vị: ghép lõi mô phỏng (sim) với hệ xương (rig).
 * Tất cả mảnh nằm trong 1 atlas ⇒ batch tốt.
 */
import Phaser from 'phaser';
import { Rig } from '../rig/rig';
import { PartFactory, PARTS_ATLAS } from '../art/parts';
import { SHADOW_KEY } from './arena';
import { buildAiCharacterRig, buildAiEnemyRigFromParts, buildAiRigFromParts, buildCharacterRig, buildEnemyRig, TARGET_CHAR_HEIGHT } from '../art/character';
import {
  AI_ENEMY_SHEETS,
  AI_SHEETS,
  AI_UNIT_SHEETS,
  loadAiEnemyParts,
  loadAiParts,
  loadAiSkillForm,
  loadAiUnitParts,
  loadUltParts,
  AI_ENEMY_DRAW_SCALE,
  type AiPartSet,
} from '../art/ai-parts';
import { applyPose } from '../art/pose';
import type { Unit } from '../sim/types';
import { CLASSES, type ClassId } from '../data/classes';
import { ENEMIES, STAGE_BOSS } from '../data/enemies';
import { className, enemyName, stageBossName } from '../i18n/loc';
import { ULT_FORMS } from '../data/ultimates';
import { meleeMoveFor } from '../data/combat-moves';
import { animOf } from '../data/skill-anims';
import { skillFormById, skillFormUrl, type SkillFormDef } from '../data/wardrobe';
import type { LineupEntry } from '../sim/world';

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * CẤU TRÚC TỐI THIỂU MÀ LỚP VẼ NÀY THẬT SỰ CẦN — ĐỂ HAI CHẾ ĐỘ DÙNG CHUNG ĐƯỢC MÀ KHÔNG ÉP KIỂU
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ VÌ SAO KHAI Ở ĐÂY (bàn giao §3.4 — ĐÃ SỬA, ĐỌC TRƯỚC KHI ĐỔI LẠI):
 *
 * `UnitViews` là lớp vẽ DÙNG CHUNG cho HAI chế độ có hai kiểu đơn vị KHÁC NHAU:
 *   · Game TD 10-wave  : `World.units: Unit[]`        — ~40 trường (thêm stamina · ammo · shield ·
 *                        buff · skillCds · targetId · minions · orderX/Y… chỉ có nghĩa ở màn dọc)
 *   · Arena PVE        : `ArenaWorld.units: ArenaUnit[]` — 25 trường (xem `sim/arena/types.ts`)
 *
 * Bản đầu `sync(dt, world: World)` nhận thẳng `World`, nên `ArenaWorld` KHÔNG khớp và
 * `arena-battle-scene.ts` phải ép kiểu `this.units as unknown as …`. Đo được khi đó (`tsc`):
 *   `Type '{ units; time }' is missing the following properties from type 'World':
 *    ultState, convertedOwners, lastBombZone, fireZones, and 125 more.`
 * Ép kiểu `as unknown as` là chỗ TypeScript NGỪNG kiểm — ai đó truyền nhầm một object thiếu `.time`
 * vẫn biên dịch được, và lỗi chỉ lộ ra giữa trận dưới dạng `undefined` chảy vào phép vẽ.
 *
 * CÁCH ĐÚNG: khai ĐÚNG thứ lớp vẽ đọc. `sync` chỉ chạm `world.units` + `world.time`, và trong mỗi
 * đơn vị chỉ 24 trường dưới đây (đã `grep` toàn file xác nhận: `u.<trường>` xuất hiện đúng tập này).
 * `Pick<Unit, …>` (KHÔNG chép tay danh sách) giữ `Unit` là nguồn sự thật duy nhất: đổi kiểu một
 * trường trong `sim/types.ts` là lỗi biên dịch ngay tại đây, không âm thầm lệch.
 *
 * ⚠️ ĐIỀU KIỆN ĐỂ CÁCH NÀY ĐÚNG: `ArenaUnit` phải có ĐỦ 24 trường với KIỂU KHỚP. Nó có — cả hai
 * dùng chung `Team` · `UnitState` · `Stats`, và `sim/arena/types.ts` ghi rõ 25 trường của
 * `ArenaUnit` là "HỢP ĐỒNG VỚI `src/render/unitview.ts`, KHÔNG PHẢI cho đủ". Thêm một trường vào
 * `Pick` mà `ArenaUnit` không có ⇒ `tsc` báo ngay ở `arena-battle-scene.ts` (mất luôn ép kiểu nghĩa
 * là TypeScript kiểm THẬT), chứ không đợi tới lúc chạy game.
 */
export type UnitViewUnit = Pick<
  Unit,
  | 'alive' | 'attackMove' | 'base' | 'castAnim' | 'castDur' | 'castT0'
  | 'deadAt' | 'facing' | 'hitFlash' | 'hp' | 'id' | 'isBoss' | 'isClone'
  | 'isConverted' | 'isElite' | 'kind' | 'mana' | 'radius' | 'state' | 'swing'
  | 'team' | 'ultUntil' | 'x' | 'y'
>;

/**
 * THẾ GIỚI TỐI THIỂU MÀ `sync()` CẦN — `{ units; time }` và KHÔNG GÌ KHÁC.
 *
 * ⚠️ KHÔNG nới thành `World`: `ArenaWorld` không có 125 trường còn lại (xem chú thích trên). Cũng
 * KHÔNG dùng `ArenaWorldLike` của `render/arena-camera.ts` — import ngược từ `render/` vào đây sẽ
 * biến lớp vẽ dùng chung thành phụ thuộc vào một chế độ, đúng thứ kiến trúc này đang tránh.
 */
export interface UnitViewsWorld {
  units: UnitViewUnit[];
  time: number;
}

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * KHO ẢNH AI DÙNG CHUNG GIỮA MỌI `UnitViews` — VÌ MỖI CHẾ ĐỘ CÓ MỘT INSTANCE RIÊNG
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ BUG ĐÃ SẬP (người dùng báo khi chơi thử): **Arena vẽ nhân vật bằng SVG, không dùng ảnh AI.**
 *
 * Nguyên nhân ĐO ĐƯỢC: `Game` nạp ảnh AI qua `this.scene.units.preloadAll()` — tức CHỈ cho instance
 * của scene 10-wave. `ArenaBattleScene` tạo `new UnitViews(this)` là một instance RIÊNG, và nó
 * **không bao giờ gọi `preloadAll()`** ⇒ `aiParts` rỗng ⇒ `buildView` rơi xuống nhánh
 * `buildCharacterRig` (vector). Số đo trong Chrome thật:
 *     chế độ 10-wave : aiLoaded = 5, enemyAiLoaded = 8   ⇒ có ảnh AI
 *     Arena          : aiLoaded = 0, enemyAiLoaded = 0   ⇒ TOÀN BỘ là hình vector
 *
 * ⚠️ VÌ SAO KHÔNG GỌI `preloadAll()` LẦN THỨ HAI CHO ARENA: `PartFactory.initAtlas()` **dùng lại**
 * `PARTS_ATLAS` đã có (atlas là texture DÙNG CHUNG toàn cục, xem `initAtlas`), nhưng `loadAiParts()`
 * vẽ mảnh vào atlas đó và `PartFactory.cache` là **cache RIÊNG của từng instance**. Nạp lần hai ⇒
 * vẽ lại toàn bộ ~120 mảnh vào CÙNG một atlas 3072² ⇒ phình gấp đôi (mỗi lần `refresh()` đã ~37 MB)
 * và có nguy cơ ĐẦY atlas ⇒ mảnh sau bị bỏ ⇒ vỡ hình.
 *
 * ⚠️ VÌ SAO CHIA SẺ `AiPartSet` LÀ AN TOÀN (đã kiểm bằng mã nguồn, không phải phỏng đoán):
 *   · `AiPart.key` chỉ là **TÊN FRAME** trong atlas; `AiPartSet` là dữ liệu thuần (key + kích thước
 *     + điểm khớp), không giữ tham chiếu tới scene/ factory nào.
 *   · Sprite được tạo bằng `this.scene.add.sprite(x, y, PARTS_ATLAS, p.key)` — tra thẳng theo TÊN
 *     FRAME trong atlas DÙNG CHUNG, **không đi qua `factory.cache`**. Nên instance thứ hai dùng được
 *     ngay các frame do instance thứ nhất tạo.
 *   · Atlas chỉ mất khi `TextureManager` của GAME bị huỷ; `scene.stop()` không xoá texture toàn cục.
 *
 * ⚠️ NẠP MỘT LẦN CHO CẢ APP, KHÔNG PHẢI MỘT LẦN CHO MỖI CHẾ ĐỘ. Đây là lý do cache nằm ở
 * module-level chứ không phải trong class: có thêm chế độ thứ ba (PVP hot-seat) thì nó cũng chỉ
 * nạp art một lần.
 */
const sharedAiParts = new Map<ClassId, AiPartSet>();
const sharedAiEnemies = new Map<string, AiPartSet>();
const sharedAiUlt = new Map<string, AiPartSet>();
const sharedAiForms = new Map<string, AiPartSet>();
const sharedAiUnits = new Map<string, AiPartSet>();

/**
 * KHOÁ ĐƠN VỊ CÓ ART AI: mọi `ClassId`, CỘNG các đơn vị KHÔNG-phải-class nhưng có sheet riêng
 * (`'farmer'` — xem `AI_UNIT_SHEETS` trong `art/ai-parts.ts`).
 *
 * ⚠️ VÌ SAO LÀ UNION CHỨ KHÔNG PHẢI `string`: union buộc mọi nơi dùng (`previewRig`, HUD, scene) phải
 * kể tên `'farmer'` một cách TƯỜNG MINH. Nếu để `string` thì một lỗi gõ (`'farmmer'`) sẽ biên dịch
 * được và chỉ lộ ra lúc chạy dưới dạng nông dân rơi về rig quái — đúng loại bug im lặng đang sửa.
 */
export type UnitArtKind = ClassId | 'farmer';

/**
 * ĐƠN VỊ KHÔNG-PHẢI-CLASS NÀY CÓ ART RIÊNG KHÔNG (nông dân)? Trả `true` ⇒ `buildView` dựng bằng
 * đường rig NHÂN VẬT (chiều cao `TARGET_CHAR_HEIGHT`) thay vì rơi xuống nhánh QUÁI/ vector.
 */
function isUnitArtKind(kind: string): kind is 'farmer' {
  return Object.prototype.hasOwnProperty.call(AI_UNIT_SHEETS, kind);
}

/**
 * NHỊP NHÁY TRẮNG KHI TRÚNG ĐÒN — **NHÁY**, KHÔNG "TÔ TRẮNG" LIÊN TỤC (lần/giây).
 *
 * Người dùng 02/10/2026 (nguyên văn): *"Khi nhân vật bị tấn công ở arena, nhân vật bị màu trắng hơi bị
 * lâu, ngoài ra khi quá nhiều quái tấn công bị nhân vật bị trắng xoá chứ không phải nhấp nháy"*.
 *
 * 🔢 HAI NGUYÊN NHÂN, SỬA Ở HAI TẦNG — ĐỪNG SỬA MỘT CHỖ RỒI TƯỞNG XONG:
 *   · "hơi bị lâu" — TẦNG SIM: Arena tắt `hitFlash` bằng `dt` (1 giây) thay vì `dt × 4` (0,25 giây)
 *     như game 10-wave ⇒ mỗi đòn tô trắng 0,65 giây. Nay dùng `ARENA_HIT_FLASH_FADE` (`sim/arena/world.ts`).
 *   · "trắng xoá chứ không nhấp nháy" — TẦNG VẼ: điều kiện cũ `u.hitFlash > 0.35` là trạng thái BẬT/
 *     TẮT thuần: hễ đang trong cửa sổ đó thì khung nào cũng tô kín màu trắng. Mà MỖI cú đánh đều đặt
 *     `hitFlash = 1`, nên bị nhiều quái đánh thì nó ĐỨNG Ở 1 vĩnh viễn ⇒ nhân vật thành một khối trắng
 *     đặc, không bao giờ "nháy". Nay pha sáng/tối lấy từ `flashOn(simTime)` ⇒ có nhịp thật.
 *
 * ⚠️ 14 Hz (mỗi pha ~36 ms): đủ nhanh để đọc ra "nháy" chứ không phải "nhấp nháy chậm", mà vẫn thấy
 * được từng nhịp ở 30–60 fps (một khung hình ~16–33 ms). Chậm hơn (~6 Hz) thì giống "đèn báo"; nhanh
 * hơn (~25 Hz) thì ở 30 fps sẽ bị nhoè thành màu xám — mất cả hai tác dụng.
 */
const FLASH_BLINK_HZ = 14;

/**
 * KHUNG HÌNH NÀY CÓ ĐANG Ở PHA SÁNG CỦA NHỊP NHÁY KHÔNG — 50% sáng/ 50% tắt, suy từ THỜI GIAN SIM.
 *
 * ⚠️ VÌ SAO LẤY PHA TỪ `simTime` CHỨ KHÔNG PHẢI TỪ `hitFlash`: `hitFlash` bị mỗi cú đánh đặt lại về 1
 * nên nó KHÔNG đếm được nhịp (đó chính là bug "trắng xoá"). `simTime` chạy đều bất kể bị đánh bao nhiêu
 * lần, nên nhịp nháy vẫn đều khi bị cả bầy quái vây.
 * Hàm thuần, tách ra để khoá được bằng test cấu trúc (file này import Phaser nên không nạp được trong
 * vitest — xem chú thích ở `tests/arena-render.test.ts`).
 */
function flashOn(simTime: number): boolean {
  return Math.floor(simTime * FLASH_BLINK_HZ * 2) % 2 === 0;
}

interface UnitView {
  rig: Rig;
  sprites: Phaser.GameObjects.Sprite[];
  shadow: Phaser.GameObjects.Image;
  seed: number;
  hpBg: Phaser.GameObjects.Rectangle;
  hpBar: Phaser.GameObjects.Rectangle;
  manaBar: Phaser.GameObjects.Rectangle;
  nameTag?: Phaser.GameObjects.Text;
  /** `kind` của đơn vị (class id với tướng) — để dựng lại view khi đổi dáng theo class */
  kind: string;
  /**
   * VÒNG MÀU PHE dưới chân đơn vị — CHỈ tạo khi `setTeamMarkers(true)` (Arena bật, game 10-wave TẮT).
   * Xem `setTeamMarkers` để hiểu vì sao cần.
   */
  teamRing?: Phaser.GameObjects.Ellipse;
  /**
   * ULTIMATE BIẾN HÌNH đã dựng rig theo art nào (`''` = hình thường).
   * Người dùng: *"Strength dùng ult biến hình mà không đổi hình ảnh nhân vật thành Dã trư khổng lồ?"*
   * ⇒ khi ult bật/tắt phải DỰNG LẠI rig (không thể đổi 1 mảnh), nên view ghi nhớ đang ở dạng nào để
   * biết lúc nào cần dựng lại.
   */
  ultArt: string;
}

export class UnitViews {
  private views = new Map<number, UnitView>();
  readonly factory: PartFactory;
  private lineup: LineupEntry[] = [];
  /**
   * HỆ SỐ CỠ NHÂN VẬT — Arena cần nhân vật NHỎ HƠN (xem `setUnitScale`). Mặc định 1 ⇒ game 10-wave
   * KHÔNG đổi một pixel nào (file này dùng chung cho cả hai chế độ).
   */
  private unitScale = 1;
  /**
   * BẬT DẤU PHE (vòng màu dưới chân + đổi màu NHÃN TÊN theo phe). Mặc định TẮT.
   *
   * ⚠️⚠️ VÌ SAO CẦN — NGƯỜI DÙNG BÁO KHI CHƠI THỬ: *"tôi không thể phân biệt được địch với ta, rất
   * khó nhìn"*. Trong Arena CẢ HAI PHE dùng CÙNG art AI và cùng bộ class (chiến trường 2 phe đối
   * xứng), nên hai tướng đứng cạnh nhau chỉ khác chiều quay — mắt không thể đọc ra ai là ai.
   *
   * ⚠️ Mặc định TẮT vì file này DÙNG CHUNG với game 10-wave: ở đó quái vào từ 4 cạnh, hình dáng đã
   * khác hẳn tướng, nên tô thêm màu phe chỉ làm rối màn. Arena bật bằng `setTeamMarkers(true)`.
   */
  private teamMarkers = false;
  /**
   * Hiện thanh mana? Mặc định `true` (game 10-wave). Arena gọi `setManaBarVisible(false)` — xem chú
   * thích phương thức đó để biết vì sao Arena không cần mana.
   */
  private manaBarVisible = true;
  /** ảnh AI đã nạp cho từng class (nếu có) */
  private aiParts = new Map<ClassId, AiPartSet>();
  /** ảnh AI đã nạp cho ULTIMATE BIẾN HÌNH (khoá = `art` trong `ULT_FORMS`) */
  private ultParts = new Map<string, AiPartSet>();
  /** ảnh AI đã nạp cho từng loại quái (nếu có) */
  private aiEnemies = new Map<string, AiPartSet>();
  /**
   * ẢNH AI ĐÃ NẠP CHO ĐƠN VỊ KHÔNG-PHẢI-CLASS (nông dân — khoá `'farmer'`).
   * Tách khỏi `aiEnemies` vì hai đường này KHÁC NHAU: quái dựng rig theo bán kính va chạm, còn nông
   * dân dựng rig NHÂN VẬT (xem `buildView`). Trộn vào một map là mất đường phân biệt.
   */
  private aiUnits = new Map<string, AiPartSet>();
  /**
   * DÁNG (tab Trang phục) ĐANG CHỌN THEO CLASS — `classId → id dáng`.
   * Lấy từ `meta.formByClass`; `game.ts` đẩy vào bằng `setForms()`.
   */
  private forms: Partial<Record<ClassId, string>> = {};
  /** ảnh dáng đã cắt xong, khoá `"<class>:<dáng>"` (nạp theo nhu cầu, xem `ensureForm`) */
  private aiForms = new Map<string, AiPartSet>();
  /** lỗi của từng dáng (ảnh thiếu ô/ atlas đầy) để UI báo cho người dùng */
  private formIssues = new Map<string, string[]>();
  /** thống kê để công cụ kiểm chứng biết art AI có đang bật không */
  aiLoaded = 0;
  /** số loại quái đang dùng ảnh AI */
  enemyAiLoaded = 0;
  /**
   * SỐ ĐƠN VỊ KHÔNG-PHẢI-CLASS đang dùng ảnh AI (nông dân).
   * Công cụ kiểm chứng đọc số này để biết "nông dân có art thật" — thay vì tin vào texture key.
   */
  unitAiLoaded = 0;
  /** true nếu lần dựng gần nhất dùng ảnh AI */
  aiUsed = false;

  constructor(private scene: Phaser.Scene) {
    this.factory = new PartFactory(scene);
  }

  /**
   * Nạp ảnh AI cho mọi class + mọi loại quái CÓ ảnh, cùng các DÁNG đang mặc (tab Trang phục).
   * Gọi 1 lần trước khi vào trận. Thiếu ảnh nào thì loại đó lùi về art vector.
   */
  async preloadAll(onProgress?: (done: number, total: number, label: string) => void): Promise<void> {
    /*
     * TIẾN TRÌNH THẬT — đếm theo SỐ VIỆC, không ước lượng theo thời gian.
     *
     * Người dùng: *"thêm màn hình Load… loading có progress bar nhé"*.
     * Tổng = số class + số loại quái + số dáng đang mặc. Mỗi ảnh nạp xong (kể cả lỗi) là +1 ⇒ thanh
     * chạy đúng nhịp thật, không nhảy giật rồi đứng im.
     */
    const total =
      Object.keys(AI_SHEETS).length +
      Object.keys(AI_ENEMY_SHEETS).length +
      Object.keys(AI_UNIT_SHEETS).length +
      Object.keys(this.forms).length;
    let done = 0;
    const tick = (label: string): void => {
      done++;
      onProgress?.(done, total, label);
    };
    // từng ảnh một: 1 ảnh hỏng KHÔNG được làm chết cả bộ art
    const safe = async <T>(label: string, fn: () => Promise<T>): Promise<T | null> => {
      try {
        return await fn();
      } catch (err) {
        console.warn(`[art] lỗi nạp ${label}:`, err);
        return null;
      }
    };

    // GOM LÔ: nạp hàng trăm mảnh mà chỉ đẩy atlas lên GPU 1 lần (xem PartFactory.beginBatch)
    this.factory.beginBatch();
    /*
     * NHƯỜNG MAIN THREAD giữa các ảnh.
     * Xử lý pixel (tách nền + lọc khối + cắt viền) là việc ĐỒNG BỘ nặng; nếu chạy
     * liền một mạch thì trình duyệt không có cơ hội vẽ lại ⇒ banner "Vào trận" tuy
     * đã bật nhưng vẫn "đứng hình" nhiều giây. Nhường 1 nhịp sau mỗi ảnh là đủ.
     */
    const breathe = (): Promise<void> => new Promise((r) => setTimeout(r, 0));
    for (const classId of Object.keys(AI_SHEETS) as ClassId[]) {
      // đã nạp ở instance khác (chế độ khác) ⇒ dùng lại, KHÔNG vẽ lại vào atlas dùng chung
      const cached = sharedAiParts.get(classId);
      if (cached) {
        this.aiParts.set(classId, cached);
        this.aiLoaded++;
        tick(`class ${classId}`);
        continue;
      }
      const set = await safe(`class ${classId}`, () => loadAiParts(this.factory, classId));
      if (set) {
        this.aiParts.set(classId, set);
        sharedAiParts.set(classId, set);
        this.aiLoaded++;
      }
      tick(`class ${classId}`);
      await breathe();
    }
    for (const enemyId of Object.keys(AI_ENEMY_SHEETS)) {
      const cached = sharedAiEnemies.get(enemyId);
      if (cached) {
        this.aiEnemies.set(enemyId, cached);
        this.enemyAiLoaded++;
        tick(`quái ${enemyId}`);
        continue;
      }
      const set = await safe(`quái ${enemyId}`, () => loadAiEnemyParts(this.factory, enemyId));
      if (set) {
        this.aiEnemies.set(enemyId, set);
        sharedAiEnemies.set(enemyId, set);
        this.enemyAiLoaded++;
      }
      tick(`quái ${enemyId}`);
      await breathe();
    }
    /*
     * ĐƠN VỊ KHÔNG-PHẢI-CLASS CÓ ART RIÊNG (nông dân Arena).
     *
     * ⚠️ VÌ SAO NẠP Ở ĐÂY CHỨ KHÔNG "NẠP THEO NHU CẦU LÚC VẼ": người chơi có thể MUA NÔNG DÂN ngay
     * giây đầu trận, và nạp ảnh là việc BẤT ĐỒNG BỘ (cắt 9 ô, tách nền). Không nạp trước thì khung
     * hình đầu tiên nông dân hiện bằng rig quái rồi "đổi hình" — đúng loại nháy hình khó chịu mà repo
     * đã ghi cho vụ art AI của Arena. `previewRig`/ `ensureUnitArt` vẫn nạp theo nhu cầu như lưới an
     * toàn cho đường kéo-thả sớm.
     */
    for (const unitId of Object.keys(AI_UNIT_SHEETS)) {
      const cached = sharedAiUnits.get(unitId);
      if (cached) {
        // ⚠️ chỉ đếm nếu instance NÀY chưa có: `ensureUnitArt` có thể đã nạp trước đó (kéo-thả sớm)
        if (!this.aiUnits.has(unitId)) this.unitAiLoaded++;
        this.aiUnits.set(unitId, cached);
        tick(`đơn vị ${unitId}`);
        continue;
      }
      const set = await safe(`đơn vị ${unitId}`, () => loadAiUnitParts(this.factory, unitId));
      if (set) {
        this.aiUnits.set(unitId, set);
        sharedAiUnits.set(unitId, set);
        this.unitAiLoaded++;
      }
      tick(`đơn vị ${unitId}`);
      await breathe();
    }
    /*
     * DÁNG ĐANG MẶC (tab Trang phục): nạp 1 lần cho mỗi class có chọn — nhiều nhất 5 ảnh,
     * KHÔNG nạp cả thư mục (người dùng có thể thả hàng chục ảnh; xem `loadAiSkillForm`).
     */
    await this.ensureForms();
    /*
     * ART ULTIMATE BIẾN HÌNH (Dã trư khổng lồ…). Nạp 1 lần cho mỗi `art` khai trong `ULT_FORMS`;
     * chưa có ảnh ⇒ bỏ qua im lặng, `buildView` tự lùi về hình class (game không bao giờ vỡ hình).
     */
    await this.ensureUltForms(safe, breathe);
    this.factory.endBatch();
  }

  /**
   * NẠP ART CHO MỌI ULTIMATE BIẾN HÌNH (`ULT_FORMS`).
   * Chạy trong `preloadViews` ⇒ nằm cùng lô nạp với dáng/ quái, không tạo request rời rạc lúc vào trận.
   */
  private async ensureUltForms(
    safe: <T>(label: string, fn: () => Promise<T>) => Promise<T | null>,
    breathe: () => Promise<void>,
  ): Promise<void> {
    for (const form of Object.values(ULT_FORMS)) {
      if (!form || this.ultParts.has(form.art)) continue;
      const cached = sharedAiUlt.get(form.art);
      if (cached) {
        this.ultParts.set(form.art, cached);
        continue;
      }
      const set = await safe(`ult ${form.art}`, () => loadUltParts(this.factory, form.art));
      if (set) {
        this.ultParts.set(form.art, set);
        sharedAiUlt.set(form.art, set);
      }
      await breathe();
    }
  }

  // ------------------------------------------------------------ DÁNG (tab Trang phục)

  /** Đặt dáng đang mặc theo class (từ `meta.formByClass`). Gọi trước khi dựng rig. */
  setForms(forms: Partial<Record<ClassId, string>>): void {
    this.forms = { ...forms };
  }

  /**
   * ĐẶT HỆ SỐ CỠ CHO MỌI NHÂN VẬT/ QUÁI (1 = cỡ gốc của game 10-wave).
   *
   * ⚠️ VÌ SAO CẦN: Arena đã co map về CÙNG CỠ màn vượt ải (1000×600) và 3 land chỉ còn cách nhau
   * 180 đơn vị. Người dùng: *"nhân vật ở Arena cũng nhỏ tương xứng đảm bảo nhiều tướng, nhiều quái,
   * nhiều nông dân cũng không bị kẹt hay vướng"*. Cỡ gốc 104 đơn vị gần bằng cả nửa bề dày land
   * (62) ⇒ vài đơn vị là chạm nhau và tràn sang land bên cạnh.
   *
   * ⚠️ VÌ SAO ÁP Ở TẦNG `UnitViews` CHỨ KHÔNG SỬA `TARGET_CHAR_HEIGHT`: hằng số đó dùng chung với
   * màn vượt ải (sửa là đổi luôn cỡ nhân vật trận 10-wave đang chạy ổn). Hệ số ở đây là CỦA RIÊNG
   * từng chế độ, và mặc định 1 nên chế độ cũ không bị đụng.
   *
   * ⚠️ Phải nhân CẢ toạ độ mảnh (`rp.x`/`rp.y`) LẪN `scaleX/scaleY`, nếu chỉ nhân scale thì các mảnh
   * co tại chỗ và nhân vật vẫn cao đúng như cũ (tay/chân rời ra) — bài học từ cách `applyPose` trả
   * toạ độ đã giải.
   */
  /** Bật/tắt dấu phe. Xem `teamMarkers` để hiểu vì sao Arena cần mà game 10-wave không. */
  setTeamMarkers(on: boolean): void {
    if (on === this.teamMarkers) return;
    this.teamMarkers = on;
    this.reset(); // buộc dựng lại: view cũ chưa có (hoặc còn) vòng phe
  }

  /**
   * BẬT/TẮT THANH MANA — Arena TẮT, game 10-wave giữ nguyên (mặc định `true`).
   *
   * ⚠️ Người dùng: *"bỏ thanh mana của nhân vật trong arena đi nhé"*.
   *
   * ⚠️ VÌ SAO PHẢI LÀ CÔNG TẮC TƯỜNG MINH, KHÔNG ĐOÁN NGẦM: `UnitViews` là lớp vẽ DÙNG CHUNG của cả
   * hai chế độ (xem chú thích đầu file), nên không được sửa cứng hành vi cho một chế độ. Đã có tiền lệ
   * đúng khuôn này là `setTeamMarkers` (Arena bật, 10-wave tắt) — thêm một công tắc nữa giữ nguyên luật
   * "lớp vẽ không tự biết mình đang vẽ chế độ nào; SCENE nói cho nó biết".
   *
   * ⚠️ Vì sao Arena TẮT được mà không mất thông tin: tướng Arena auto dùng chiêu và KHÔNG tiêu mana
   * (xem `summonHero`/ các ult trong `sim/arena/world.ts`), nên thanh mana ở Arena chỉ hiện một vạch
   * rỗng vô nghĩa — mà vẫn chiếm chỗ và gây nhiễu trên nhân vật nhỏ (`ARENA_UNIT_SCALE` = 0,6).
   *
   * ⚠️ `reset()` là BẮT BUỘC: view đã dựng thì thanh mana là một GameObject ĐÃ TỒN TẠI; chỉ đổi cờ mà
   * không dựng lại thì thanh cũ nằm nguyên trên sân (đúng loại lỗi mà `setTeamMarkers` đã tránh).
   */
  setManaBarVisible(on: boolean): void {
    if (on === this.manaBarVisible) return;
    this.manaBarVisible = on;
    this.reset();
  }

  setUnitScale(k: number): void {
    if (!Number.isFinite(k) || k <= 0) return;
    if (k === this.unitScale) return;
    this.unitScale = k;
    this.reset(); // buộc dựng lại để bóng/ thanh máu/ nhãn tên tính lại theo cỡ mới
  }

  /** Khoá cache của 1 dáng. */
  private formKey(classId: ClassId, formId: string): string {
    return `${classId}:${formId}`;
  }

  /**
   * NẠP 1 DÁNG THEO NHU CẦU (cache cả trường hợp thất bại).
   * Trả `null` nếu: không có ảnh, ảnh hỏng, thiếu ô bắt buộc, hoặc atlas đầy ⇒ gọi nơi dựng
   * rig tự lùi về dáng gốc của class (game không bao giờ vỡ hình).
   */
  async ensureForm(classId: ClassId, formId: string): Promise<AiPartSet | null> {
    const key = this.formKey(classId, formId);
    const hit = this.aiForms.get(key);
    if (hit) return hit;
    const defects = skillFormById(classId, formId);
    if (!defects) return null;
    /*
     * `skillFormUrl` nay ĐỒNG BỘ (URL đến từ `manifest.json`, không qua bundler nữa)
     * — không cần `await`. Nó trả `null` khi manifest thiếu `src` ⇒ lùi về dáng gốc.
     */
    const url = skillFormUrl(defects);
    if (!url) return null;
    // dáng đã nạp ở instance khác (chế độ khác) ⇒ dùng lại, không vẽ lại vào atlas dùng chung
    const shared = sharedAiForms.get(key);
    if (shared) {
      this.aiForms.set(key, shared);
      return shared;
    }
    const r = await loadAiSkillForm(this.factory, classId, formId, url);
    if (r.issues.length) this.formIssues.set(key, r.issues);
    if (!r.set) return null;
    this.aiForms.set(key, r.set);
    sharedAiForms.set(key, r.set);
    return r.set;
  }

  /** Nạp mọi dáng đang mặc (mỗi class ≤ 1 ảnh). Gọi lúc boot + khi vào trận. */
  async ensureForms(): Promise<void> {
    for (const c of Object.keys(this.forms) as ClassId[]) {
      const id = this.forms[c];
      if (id) await this.ensureForm(c, id);
    }
  }

  /** Tra dáng ĐÃ nạp của 1 class — `undefined` nghĩa là dùng dáng gốc của class. */
  private loadedFormFor(classId: ClassId): AiPartSet | undefined {
    const id = this.forms[classId];
    if (!id) return undefined;
    return this.aiForms.get(this.formKey(classId, id));
  }

  /** Lỗi của 1 dáng (nếu có) — UI hiển thị "ảnh thiếu ô …". */
  issuesFor(classId: ClassId, formId: string): string[] | undefined {
    return this.formIssues.get(this.formKey(classId, formId));
  }

  /**
   * BẢO ĐẢM CÓ ART CHO 1 ĐƠN VỊ KHÔNG-PHẢI-CLASS (nông dân) — nạp theo nhu cầu.
   *
   * Vì sao cần dù `preloadAll` đã nạp trước: đường KÉO-THẢ avatar có thể hỏi rig trước khi lần nạp
   * nền xong (và ở chế độ khác thì `preloadAll` của instance này thậm chí chưa chạy lần nào). Trả
   * `null` cho mọi khoá không có trong `AI_UNIT_SHEETS` (kể cả mọi `ClassId`) — rẻ, không nạp gì.
   */
  async ensureUnitArt(id: string): Promise<AiPartSet | null> {
    if (!Object.prototype.hasOwnProperty.call(AI_UNIT_SHEETS, id)) return null;
    const hit = this.aiUnits.get(id);
    if (hit) return hit;
    const shared = sharedAiUnits.get(id);
    if (shared) {
      this.aiUnits.set(id, shared);
      this.unitAiLoaded++;
      return shared;
    }
    const set = await loadAiUnitParts(this.factory, id);
    if (!set) return null;
    this.aiUnits.set(id, set);
    sharedAiUnits.set(id, set);
    this.unitAiLoaded++;
    return set;
  }

  /**
   * DỰNG RIG XEM TRƯỚC THEO DÁNG (tab Trang phục) — VÀ CHO CẢ NÔNG DÂN.
   *
   * Khác `buildPreviewRig` ở 2 điểm:
   *   · BẤT ĐỒNG BỘ: dáng được nạp theo nhu cầu (ảnh người dùng thả vào), nên phải `await`.
   *   · KHÔNG cần bản ghi tướng: xem được dáng của class MÌNH CHƯA MUA (dùng bộ đồ mặc định).
   *
   * `formId` rỗng/ không có ảnh/ ảnh hỏng ⇒ lùi về dáng gốc của class (đúng đường của `buildView`),
   * nên hình xem trước LUÔN giống hình trong trận.
   *
   * ⚠️ `kind` nay nhận CẢ `'farmer'` (nông dân Arena) — bóng xem trước khi kéo avatar nông dân dùng
   * CHÍNH hàm này, nên nông dân phải đi qua ĐÚNG đường rig mà `buildView` dùng, nếu không bóng và
   * quân thật sẽ lệch hình/ lệch cỡ. Nới kiểu ở đây KHÔNG phá người gọi cũ: `hud.ts` truyền `ClassId`,
   * mà `ClassId` là tập con của `UnitArtKind`.
   */
  async previewRig(kind: UnitArtKind, formId?: string): Promise<Rig> {
    if (formId && kind !== 'farmer') {
      const set = await this.ensureForm(kind, formId);
      if (set) return buildAiCharacterRig(this.factory, { classId: kind }, set);
    }
    /*
     * NÔNG DÂN ĐI TRƯỚC NHÁNH CLASS — và PHẢI dùng `buildAiRigFromParts` với ĐÚNG
     * `TARGET_CHAR_HEIGHT` mà `buildView` dùng cho đơn vị không-phải-class có art (xem `buildView`).
     * Hai bên lệch cỡ là bóng "to/nhỏ hơn hình sắp ra" — đúng thứ người chơi dùng để ướm vị trí.
     */
    const unitSet = await this.ensureUnitArt(kind);
    if (unitSet) return buildAiRigFromParts(this.factory, unitSet, { targetHeight: TARGET_CHAR_HEIGHT });
    if (kind === 'farmer') {
      /*
       * CHƯA CÓ ART NÔNG DÂN (ảnh thiếu/ atlas đầy): trả rig vector XÁM TRUNG TÍNH.
       * ⚠️ Vẫn là rig quái (không có rig người vector riêng) NHƯNG màu trung tính — KHÔNG mượn màu
       * trùm `STAGE_BOSS` như bug cũ, nên không còn hiểu nhầm "nông dân là quái".
       */
      return buildEnemyRig(this.factory, { color: 0xcccccc, accent: 0x888888, radius: 20 });
    }
    const cls = CLASSES[kind];
    if (!cls) return buildEnemyRig(this.factory, { color: 0xcccccc, accent: 0x888888, radius: 20 });
    const ai = this.aiParts.get(kind);
    if (ai) return buildAiCharacterRig(this.factory, { classId: kind }, ai);
    return buildCharacterRig(this.factory, { classId: kind, outfitColor: cls.color });
  }

  /**
   * View của 1 đơn vị — CÔNG KHAI để tầng cảnh (scene) dò vùng bấm của tướng.
   * Trước đây là `private` nên `pickHeroAt()` không lấy được hộp bao thật của rig.
   */
  viewOf(unitId: number): UnitView | undefined {
    return this.views.get(unitId);
  }

  setLineup(lineup: LineupEntry[]): void {
    this.lineup = lineup;
  }

  /** Dựng phần thân cho 1 đơn vị. */
  private buildView(u: UnitViewUnit): UnitView {
    let rig: Rig;
    /** art ultimate đã dùng cho view này (`''` = hình thường) */
    let ultArtKey = '';
    /*
     * ⚠️⚠️ CHỌN DÁNG THEO **CLASS**, KHÔNG THEO PHE — SỬA THEO PHẢN HỒI NGƯỜI DÙNG:
     * *"Tướng pve địch cũng sẽ là các class nhân available chứ không phải hình quái, skill hay chiêu
     * thức tương tự logic người chơi"*.
     *
     * BẢN CŨ: `u.team === 'hero' ? CLASSES[u.kind] : undefined` ⇒ TƯỚNG PHE ĐỊCH (team `'enemy'`)
     * rơi xuống nhánh quái, tra `ENEMIES['agility']` → `undefined` → lùi về **`STAGE_BOSS` (hình
     * quái)**. ĐO ĐƯỢC đúng lúc người dùng phàn nàn: log world cho
     * `right/agility(H) x=109` — tướng địch LÀ class `agility` trong sim, nhưng màn hình vẽ nó bằng
     * art quái. Đây là lỗi TRÌNH BÀY nặng: người chơi không thể biết phe địch có tướng.
     *
     * NAY: hỏi thẳng "kind này có phải CLASS không". Đúng cho cả hai chế độ:
     *   · Arena: hai phe đều có tướng class ⇒ cả hai dùng art class (chỉ khác vòng màu phe).
     *   · 10-wave: tướng ta kind = classId ⇒ art class; quái kind = id quái ⇒ không có trong CLASSES
     *     ⇒ vẫn vào nhánh quái như cũ (KHÔNG đổi gì).
     *
     * ⚠️ VẪN PHẢI GIỮ LUẬT CŨ: "Mẹ Thiên Hạ" đẻ ra đệ tử mang team 'hero' nhưng kind KHÔNG phải
     * class (`brat`) ⇒ `CLASSES['brat']` undefined ⇒ nó đi nhánh quái. Việc tra theo `kind` TỰ ĐỘNG
     * xử lý đúng ca này, nên không cần điều kiện theo `team` nữa.
     */
    const cls = CLASSES[u.kind as ClassId];
    if (cls) {
      /*
       * DÁNG ĐANG MẶC (tab Trang phục) ĐI TRƯỚC ảnh class. Dáng là ẢNH TRỌN BỘ (đầu · thân ·
       * tay · chân · ủng) do người dùng vẽ ⇒ khi mặc dáng thì không gắn thêm gì lên đầu.
       * PHỤ KIỆN ĐÃ XOÁ HẲN ⇒ đường còn lại: ảnh dáng → ảnh class → rig vector màu class.
       */
      const formSet = this.loadedFormFor(u.kind as ClassId);
      const ai = this.aiParts.get(u.kind as ClassId);
      /*
       * ULTIMATE BIẾN HÌNH — ĐI TRƯỚC MỌI DÁNG KHÁC.
       * Người dùng: *"Sao ví dụ như Strength dùng ult biến hình mà không đổi hình ảnh nhân vật thành
       * Dã trư khổng lồ?"* ⇒ trong lúc ult hiệu lực, nhân vật phải mang art của DÃ TRƯ, không phải
       * hình Dân tập gym (kể cả khi đang mặc dáng ở tab Trang phục).
       * `ULT_FORMS` khai tập trung ở `data/ultimates.ts` (nguồn sự thật duy nhất).
       */
      const ultForm = u.ultUntil > 0 ? ULT_FORMS[u.kind as ClassId] : undefined;
      const ultSet = ultForm ? this.ultParts.get(ultForm.art) : undefined;
      if (ultSet && ultForm) {
        rig = buildAiCharacterRig(this.factory, { classId: u.kind as ClassId }, ultSet, { scaleMult: ultForm.scale });
        this.aiUsed = true;
        ultArtKey = ultForm.art;
      } else if (formSet) {
        rig = buildAiCharacterRig(this.factory, { classId: u.kind as ClassId }, formSet);
        this.aiUsed = true;
      } else if (ai) {
        rig = buildAiCharacterRig(this.factory, { classId: u.kind as ClassId }, ai);
        this.aiUsed = true;
      } else {
        // art vector: áo nhuộm MÀU CLASS ⇒ nhìn là biết class nào
        rig = buildCharacterRig(this.factory, { classId: u.kind as ClassId, outfitColor: cls.color });
      }
    } else if (isUnitArtKind(u.kind)) {
      /*
       * ĐƠN VỊ KHÔNG-PHẢI-CLASS CÓ ART RIÊNG (NÔNG DÂN Arena) — SỬA BUG "NÔNG DÂN VẼ BẰNG ART TRÙM".
       *
       * ⚠️⚠️ VÌ SAO NHÁNH NÀY PHẢI ĐỨNG TRƯỚC NHÁNH QUÁI: `u.kind = 'farmer'` không có trong `CLASSES`
       * nên trước đây rơi thẳng xuống `ENEMIES['farmer'] ?? STAGE_BOSS` ⇒ nông dân mang **rig quái màu
       * trùm**. Người chơi thấy một con quái nhỏ đi đào mỏ. Đây là bug CÓ SẴN, không phải hệ quả của
       * đợt refactor nào.
       *
       * ⚠️ DÙNG `buildAiRigFromParts` VỚI `TARGET_CHAR_HEIGHT` (KHÔNG phải `buildAiEnemyRigFromParts`):
       * nông dân là NGƯỜI, và `updateView` đã đặt thanh máu/ nhãn của mọi đơn vị `team === 'hero'` theo
       * cỡ nhân vật (yOff -104, rộng 40) — dựng nông dân nhỏ bằng bán kính va chạm sẽ để thanh máu
       * lơ lửng tít trên đầu. Dùng CÙNG chiều cao với tướng thì bóng xem trước (cũng đi qua
       * `previewRig` → cùng hàm, cùng chiều cao) khớp TUYỆT ĐỐI với quân thật.
       *
       * ⚠️ Chưa nạp được art (ảnh thiếu/ atlas đầy) ⇒ rig vector XÁM TRUNG TÍNH, KHÔNG mượn màu trùm.
       * Vào trận, `game.ts` gọi `units.reset()` sau khi `preloadAll` xong nên view sẽ tự dựng lại bằng
       * art thật (cùng cơ chế đã có cho nhánh class).
       */
      const unitArt = this.aiUnits.get(u.kind);
      if (unitArt) {
        rig = buildAiRigFromParts(this.factory, unitArt, { targetHeight: TARGET_CHAR_HEIGHT });
        this.aiUsed = true;
      } else {
        rig = buildEnemyRig(this.factory, { color: 0xcccccc, accent: 0x888888, radius: u.radius });
      }
    } else {
      const aiEnemy = this.aiEnemies.get(u.kind);
      if (aiEnemy) {
        // QUÁI có ảnh AI ⇒ dùng ảnh thật, cỡ suy từ radius như rig vector (+ độ phóng to CHỈ ĐỂ VẼ
        // của boss cuối — xem `AI_ENEMY_DRAW_SCALE`)
        rig = buildAiEnemyRigFromParts(this.factory, aiEnemy, {
          radius: u.radius,
          isBoss: u.isBoss,
          scaleMult: AI_ENEMY_DRAW_SCALE[u.kind] ?? 1,
        });
      } else {
        const def = ENEMIES[u.kind] ?? STAGE_BOSS;
        rig = buildEnemyRig(this.factory, {
          color: def.color,
          accent: def.accent,
          radius: u.radius,
          isBoss: u.isBoss,
        });
      }
    }

    const sprites: Phaser.GameObjects.Sprite[] = [];
    for (const p of rig.parts) {
      const s = this.scene.add.sprite(u.x, u.y, PARTS_ATLAS, p.key).setOrigin(0.5, 0.5);
      sprites.push(s);
    }

    /*
     * ⚠️ CỠ THANH MÁU/ ĐỘ CAO NHÃN TÊN LẤY TỪ `barMetrics()` — MỘT CHỖ DUY NHẤT, dùng chung với
     * `updateView`. Xem chú thích `barMetrics` về hai bug đã sập vì hai đường tự tính riêng.
     */
    const { w, yOff } = this.barMetrics(u);
    // bóng đổ giúp nhân vật "đứng trên mặt đất" thay vì lơ lửng
    const shadow = this.scene.add
      .image(u.x, u.y + 2, SHADOW_KEY)
      .setDisplaySize(u.radius * 3.2, u.radius * 1.35)
      .setAlpha(0.35);
    /*
     * VÒNG MÀU PHE — tín hiệu đọc được ở MỌI khoảng cách, không phụ thuộc art (xem `teamMarkers`).
     * Đặt ngay dưới chân, to hơn bóng một chút để không bị bóng che, và depth = 0 (dưới nhân vật,
     * trên sàn ở depth âm).
     */
    const teamRing = this.teamMarkers
      ? this.scene.add
          .ellipse(u.x, u.y + 3, u.radius * 4.2, u.radius * 1.9, u.team === 'hero' ? 0x38bdf8 : 0xf87171, 0.28)
          .setStrokeStyle(2, u.team === 'hero' ? 0x7dd3fc : 0xfca5a5, 0.95)
      : undefined;
    const hpBg = this.scene.add.rectangle(u.x, u.y + yOff, w, 5, 0x1c1917, 0.85).setDepth(1);
    const hpBar = this.scene.add.rectangle(u.x - w / 2, u.y + yOff, w, 5, 0x22c55e).setOrigin(0, 0.5).setDepth(2);
    const manaBar = this.scene.add.rectangle(u.x - w / 2, u.y + yOff + 6, w, 3, 0x3b82f6).setOrigin(0, 0.5).setDepth(2);
    // ⚠️ Công tắc `manaBarVisible`: Arena TẮT (tướng Arena không tiêu mana) — xem `setManaBarVisible`.
    manaBar.setVisible(this.manaBarVisible && u.team === 'hero');

    let nameTag: Phaser.GameObjects.Text | undefined;
    if (cls) {
      const entry = this.lineup.find((l) => l.classId === u.kind);
      nameTag = this.scene.add
        .text(u.x, u.y + yOff - 10, entry ? className(entry.classId) : className(cls.id), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          // có dấu phe ⇒ nhãn tên theo màu phe (tín hiệu THỨ HAI, không phụ thuộc viền)
          color: this.teamMarkers ? (u.team === 'hero' ? '#7dd3fc' : '#fca5a5') : '#fde68a',
        })
        .setOrigin(0.5, 1)
        .setDepth(3);
    }
    if (u.isBoss) {
      this.scene.add
        .text(u.x, u.y + yOff - 8, u.kind in ENEMIES ? enemyName(u.kind) : stageBossName(), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#fca5a5',
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 1)
        .setDepth(3)
        .setName(`boss-tag-${u.id}`);
    }

    return { rig, sprites, shadow, teamRing, seed: (u.id * 0.137) % 1, hpBg, hpBar, manaBar, nameTag, kind: u.kind, ultArt: ultArtKey };
  }

  /**
   * DỰNG LẠI VIEW CỦA MỌI ĐƠN VỊ THUỘC 1 CLASS — dùng khi người chơi ĐỔI DÁNG ở tab Trang phục.
   *
   * Vì sao phải dựng lại (không `replaceSlot` như đổi mũ): dáng là ẢNH TRỌN BỘ với tỉ lệ xương
   * do chính ảnh quyết định (xem `buildAiRigFromParts`), không phải 1 mảnh gắn thêm ⇒ phải dựng
   * lại rig. `sync()` ở khung hình sau sẽ tự tạo view mới với dáng vừa chọn.
   */
  resetClass(classId: ClassId): void {
    for (const [id, v] of this.views) {
      if (v.kind !== classId) continue;
      for (const s of v.sprites) s.destroy();
      v.shadow.destroy();
      v.hpBg.destroy();
      v.hpBar.destroy();
      v.manaBar.destroy();
      v.teamRing?.destroy();
      v.nameTag?.destroy();
      this.scene.children.getByName(`boss-tag-${id}`)?.destroy();
      this.views.delete(id);
    }
  }

  /** Art ultimate của đơn vị có ĐỔI so với lúc dựng view không (bật/ tắt biến hình). */
  private ultArtChanged(u: UnitViewUnit, view: UnitView): boolean {
    const want = u.ultUntil > 0 ? (ULT_FORMS[u.kind as ClassId]?.art ?? '') : '';
    return want !== view.ultArt;
  }

  /** Huỷ mọi đối tượng hiển thị của 1 view + xoá khỏi bảng (dùng chung cho despawn và biến hình). */
  private disposeView(id: number, v: UnitView): void {
    for (const s of v.sprites) s.destroy();
    v.shadow.destroy();
    v.hpBg.destroy();
    v.hpBar.destroy();
    v.manaBar.destroy();
    v.teamRing?.destroy();
    v.nameTag?.destroy();
    this.scene.children.getByName(`boss-tag-${id}`)?.destroy();
    this.views.delete(id);
  }

  sync(dt: number, world: UnitViewsWorld): void {
    const seen = new Set<number>();
    for (const u of world.units) {
      seen.add(u.id);
      let view = this.views.get(u.id);
      if (!view) {
        view = this.buildView(u);
        this.views.set(u.id, view);
      } else if (this.ultArtChanged(u, view)) {
        /*
         * BIẾN HÌNH KHI ULT BẬT/TẮT — PHẢI DỰNG LẠI RIG.
         *
         * Người dùng: *"Sao ví dụ như Strength dùng ult biến hình mà không đổi hình ảnh nhân vật
         * thành Dã trư khổng lồ?"* — art là bộ xương MỚI (tỉ lệ xương do ảnh quyết định), không
         * thể đổi 1 mảnh như đổi mũ ⇒ phải huỷ view cũ và dựng lại. Đây là lý do view ghi nhớ
         * `ultArt`: chỉ dựng lại đúng lúc bật/ tắt, KHÔNG dựng lại mỗi khung hình.
         */
        this.disposeView(u.id, view);
        view = this.buildView(u);
        this.views.set(u.id, view);
      }
      this.updateView(u, view, dt, world.time);
    }
    for (const [id, v] of this.views) {
      if (!seen.has(id)) this.disposeView(id, v);
    }
  }

  /**
   * CỠ THANH MÁU VÀ ĐỘ LỆCH Y CỦA NÓ/ NHÃN TÊN — **MỘT CHỖ DUY NHẤT**, dùng cho CẢ lúc tạo (`buildView`)
   * LẪN lúc cập nhật mỗi khung hình (`updateView`).
   *
   * ⚠️⚠️ VÌ SAO PHẢI GOM: HAI ĐƯỜNG TỪNG TÍNH KHÁC NHAU, VÀ ĐÓ LÀ HAI BUG THẬT.
   *
   * 1) **THIẾU `unitScale` Ở ĐƯỜNG CẬP NHẬT** — người dùng báo *"Thanh máu của nhân vật quá xa đầu nhân
   *    vật ở arena"*. `buildView` nhân `unitScale` (`w = (cls ? 40 : …) * k`, `yOff = … * k`) nhưng
   *    `updateView` thì KHÔNG. Arena đặt `unitScale = ARENA_UNIT_SCALE = 0,6` ⇒ khung hình đầu đúng,
   *    rồi MỖI KHUNG HÌNH sau đó thanh máu bị kéo về độ lệch CHƯA co (`-104` thay vì `-62,4`) ⇒ thanh
   *    máu lơ lửng ~42 đơn vị TRÊN ĐẦU nhân vật. Đây là loại bug "hai bản sao của một công thức": chỉ
   *    cần một bản được sửa là bản kia lệch, và triệu chứng chỉ lộ ở chế độ có `unitScale ≠ 1`.
   *
   * 2) **SAI VỊ TỪ Ở ĐƯỜNG CẬP NHẬT** — `buildView` hỏi "kind này có art CLASS không?" (`CLASSES[u.kind]`),
   *    còn `updateView` hỏi `u.team === 'hero'`. Hai câu hỏi KHÁC NHAU: TƯỚNG PHE ĐỊCH có art class như
   *    tướng ta nhưng `team !== 'hero'` ⇒ lúc tạo đặt thanh máu cao đúng, mỗi khung hình sau lại kéo
   *    xuống cỡ QUÁI ⇒ thanh máu nằm giữa bụng tướng địch. Chính chú thích cũ ở `buildView` đã cảnh báo
   *    đúng điều này mà bản cập nhật vẫn làm sai — dấu hiệu chắc chắn của "công thức bị chép hai lần".
   *
   * ⇒ Trả về CẢ HAI số từ một hàm, và cả hai đường gọi hàm này. Đừng chép công thức ra chỗ khác nữa.
   * `unitScale` vẫn phải nhân ở đây (không sửa `TARGET_CHAR_HEIGHT`) vì hằng số đó dùng chung với game
   * 10-wave — xem `setUnitScale`.
   */
  /**
   * ĐƠN VỊ NÀY CÓ HIỆN THANH MÁU KHÔNG — **MỘT CHỖ DUY NHẤT** cho cả lúc tạo lẫn lúc cập nhật.
   *
   * ⚠️⚠️ BUG NGƯỜI DÙNG BÁO: *"Máu tướng địch trong arena đang không có."*
   *
   * NGUYÊN NHÂN: cả hai đường dùng `u.team === 'hero' || u.isBoss || u.isElite`. Ở Arena, TƯỚNG PHE ĐỊCH
   * có art class nhưng `team === 'enemy'`, và không phải boss/ elite ⇒ thanh máu bị ẨN HOÀN TOÀN. Đây
   * đúng là cùng một lỗi "hỏi theo PHE thay vì theo ART CLASS" mà `barMetrics` đã mắc ở phần độ lệch
   * (xem mục 2 ở chú thích `barMetrics`) — chỉ khác là ở đây hậu quả là MẤT HẲN thanh máu, nặng hơn.
   *
   * ⇒ Câu hỏi đúng là "đơn vị này có art NHÂN VẬT không?" (`CLASSES[u.kind]`), vì binh thường (quái nhỏ)
   * vốn KHÔNG hiện thanh máu để sân đỡ rối — đó là chủ ý của game, không phải thiếu sót.
   * Boss/ elite vẫn hiện như cũ (chúng là mối đe doạ, phải thấy máu) — giữ nguyên hành vi cũ.
   */
  private showsHpBar(u: UnitViewUnit): boolean {
    return !!CLASSES[u.kind as ClassId] || u.isBoss || u.isElite;
  }

  private barMetrics(u: UnitViewUnit): { w: number; yOff: number } {
    /*
     * ⚠️ KHOẢNG CHỪA TRÊN ĐỈNH ĐẦU — và đây là chỗ suýt bị "sửa quá tay".
     *
     * Người dùng: *"Thanh máu của nhân vật quá xa đầu nhân vật ở arena"* ⇒ tôi sửa cho đường cập nhật
     * nhân `unitScale` (mục 1 ở chú thích trên). Sau đó người dùng thấy *"Thanh máu trên đầu lại hơi sát
     * đầu quá, cho cao lên 1 xíu"*.
     *
     * VÌ SAO "SÁT ĐẦU" LÀ ĐÚNG NHƯ ĐOÁN ĐƯỢC: `TARGET_CHAR_HEIGHT` = 104 chính là CHIỀU CAO của rig
     * nhân vật, nên `yOff = -104 * k` đặt thanh máu ĐÚNG NGANG ĐỈNH ĐẦU — khoảng chừa bằng 0, nhìn như
     * thanh máu dính vào tóc. Đó là hành vi CÓ TỪ TRƯỚC ở cả hai chế độ (ở 10-wave `k = 1` nên thanh
     * cũng nằm đúng đỉnh đầu), chỉ là ở Arena người dùng mới để ý.
     * ⇒ Thêm một khoảng chừa NHỎ, và nhân luôn `k` để nó co giãn cùng nhân vật (mọi thứ thuộc đơn vị này
     * đều theo `k` — xem `setUnitScale`). 8 đơn vị ở `k = 1` là khoảng chừa vừa mắt cho 10-wave, và
     * 4,8 đơn vị ở Arena (`k = 0,6`).
     */
    const CLEARANCE = 8;
    /*
     * ⚠️ TRA THEO ART CLASS (`CLASSES[u.kind]`), KHÔNG THEO PHE — xem mục (2) ở chú thích trên.
     * `TARGET_CHAR_HEIGHT` là chiều cao rig nhân vật, nên "có art class" mới là câu hỏi đúng.
     */
    const isCharArt = !!CLASSES[u.kind as ClassId];
    const k = this.unitScale;
    /** Độ lệch Y "gốc" (âm = lên trên), TRƯỚC khi nhân `k` và TRƯỚC khoảng chừa. */
    const base = isCharArt ? -104 : -(u.radius * 5.4);
    return {
      w: (isCharArt ? 40 : Math.max(26, u.radius * 2.2)) * k,
      yOff: (base - CLEARANCE) * k,
    };
  }

  private updateView(u: UnitViewUnit, v: UnitView, dt: number, simTime: number): void {
    const hpPct = Math.max(0, u.hp / u.base.hp);
    applyPose(v.rig, {
      state: u.state,
      t: simTime,
      swing: u.swing,
      facing: u.facing,
      hpPct,
      seed: v.seed,
      deadFor: u.alive ? 0 : simTime - u.deadAt,
      hitFlash: u.hitFlash,
      move: meleeMoveFor(u.kind, u.attackMove),
      cast: u.castAnim,
      castT: u.castAnim ? (simTime - u.castT0) / Math.max(0.05, u.castDur) : undefined,
    });

    const parts = v.rig.update();
    /*
     * LẬT NGANG Ở ĐÂY, không lật trong ma trận xương.
     * Cách đúng: đảo toạ độ x quanh trục dọc của nhân vật, đảo dấu góc xoay,
     * và bật flipX của sprite. Nếu nhân vào scaleX của xương thì ma trận có
     * định thức âm ⇒ nhân vật bị xoay 180° (lộn ngược) — đúng lỗi đã gặp.
     */
    const flip = u.facing === -1;
    /*
     * Dáng "hai tay đưa ra trước" cần vẽ TAY ĐÈ LÊN THÂN, nếu không tay trái sẽ
     * chui ra sau thân (tay z=25 < thân z=30) và người chơi chỉ thấy một tay.
     */
    const armFront = u.castAnim ? animOf(u.castAnim).frontArms === true : false;
    for (let i = 0; i < parts.length; i++) {
      const rp = parts[i];
      const s = v.sprites[i];
      if (!s) continue;
      // nhân `unitScale` cho CẢ toạ độ mảnh lẫn scale — xem chú thích `setUnitScale`
      s.x = u.x + (flip ? -rp.x : rp.x) * this.unitScale;
      s.y = u.y + rp.y * this.unitScale;
      s.rotation = flip ? -rp.rotation : rp.rotation;
      s.scaleX = rp.scaleX * this.unitScale;
      s.scaleY = rp.scaleY * this.unitScale;
      s.setFlipX(flip);
      const isArm = rp.part.slot === 'arms' || rp.part.slot === 'hands';
      s.setDepth(u.y * 10 + (armFront && isArm ? 32 : rp.z) * 0.01);
      if (!u.alive) {
        const k = Math.min(1, (simTime - u.deadAt) / 0.6);
        s.setAlpha(1 - k * 0.85);
      } else if (u.isClone) {
        /*
         * BẢN SAO PHÂN THÂN — người dùng: *"Phân thân 3 nhân vật y như agility"* ⇒ CÙNG art, chỉ
         * khác độ trong suốt để người chơi biết con nào là bản sao (và biết mình chỉ điều khiển
         * được con gốc). 0,62 đủ thấy mờ mà vẫn đọc được dáng.
         */
        s.setAlpha(0.62);
      } else {
        s.setAlpha(1);
      }
      /*
       * NHÁY TRẮNG KHI TRÚNG ĐÒN — **NHÁY THEO NHỊP** (xem `flashOn`/ `FLASH_BLINK_HZ` ở đầu file).
       *
       * ⚠️⚠️ ĐIỀU KIỆN CŨ `u.hitFlash > 0.35` LÀ BUG NGƯỜI DÙNG BÁO *"trắng xoá chứ không phải nhấp
       * nháy"*: nó là BẬT/TẮT thuần theo độ lớn, mà mỗi cú đánh lại đặt `hitFlash = 1` ⇒ bị nhiều quái
       * đánh thì `hitFlash` đứng ở 1 và nhân vật trắng đặc LIÊN TỤC. Nay pha nháy lấy từ `simTime`.
       *
       * ⚠️ KHÔNG bỏ nhánh `!u.alive`/ `isClone`/ `isConverted` phía dưới: khi pha TẮT, khối `else if`
       * này trả nhân vật về ĐÚNG màu trạng thái của nó (xác xám · bản sao tím · quân thu phục hồng),
       * chứ không phải về màu gốc — đó là lý do phép nháy chỉ được thêm vào ĐIỀU KIỆN, không tách ra ngoài.
       * Phaser 4: `setTintFill()` đã bỏ tham số ⇒ dùng `setTint` + `TintModes.FILL`.
       */
      if (u.hitFlash > 0 && flashOn(simTime)) s.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
      else if (!u.alive) s.setTint(0x888888).setTintMode(Phaser.TintModes.MULTIPLY);
      else if (u.isClone) s.setTint(0xc4b5fd).setTintMode(Phaser.TintModes.MULTIPLY); // tím nhạt = bản sao
      /*
       * QUÂN THU PHỤC — nhuộm hồng để nhìn là biết đã theo phe mình.
       * Người dùng chốt *"Sẽ trở lại làm quái"* ⇒ chỉ ĐỔI MÀU, không đổi art: khi hết hiệu lực
       * `isConverted` về false và màu tự trở lại bình thường, không cần dựng lại rig.
       */
      else if (u.isConverted) s.setTint(0xf9a8d4).setTintMode(Phaser.TintModes.MULTIPLY);
      else s.clearTint();
    }

    v.shadow.setPosition(u.x, u.y + 2).setDepth(u.y * 10 - 1).setVisible(u.alive);

    const { w, yOff } = this.barMetrics(u);
    // vòng phe bám chân đơn vị; ẩn khi chết (cùng lúc với thanh máu)
    v.teamRing?.setPosition(u.x, u.y + 3).setVisible(u.alive);
    v.hpBg.setPosition(u.x, u.y + yOff).setVisible(u.alive && this.showsHpBar(u));
    v.hpBar
      .setPosition(u.x - w / 2, u.y + yOff)
      .setDisplaySize(Math.max(0, w * hpPct), 5)
      .setVisible(u.alive && this.showsHpBar(u));
    v.hpBar.fillColor = hpPct > 0.5 ? 0x22c55e : hpPct > 0.25 ? 0xf59e0b : 0xef4444;
    const manaPct = Math.max(0, Math.min(1, u.mana / Math.max(1, u.base.mana)));
    v.manaBar.setPosition(u.x - w / 2, u.y + yOff + 6).setDisplaySize(w * manaPct, 3).setVisible(u.alive && this.manaBarVisible && u.team === 'hero');
    v.nameTag?.setPosition(u.x, u.y + yOff - 10).setVisible(u.alive);
    const bossTag = this.scene.children.getByName(`boss-tag-${u.id}`) as Phaser.GameObjects.Text | undefined;
    bossTag?.setPosition(u.x, u.y + yOff - 8).setVisible(u.alive);
    void dt;
  }

  get viewCount(): number {
    return this.views.size;
  }

  /**
   * ĐỔI NGÔN NGỮ: viết lại chữ trên NHÃN TÊN (tướng theo class · quái/ trùm) mà KHÔNG dựng lại
   * rig — chỉ `setText` nên rẻ và không nháy hình. Gọi từ `ArenaScene.refreshLang()`.
   */
  refreshNameTags(): void {
    for (const [id, v] of this.views) {
      if (v.nameTag) {
        const entry = this.lineup.find((l) => l.classId === v.kind);
        v.nameTag.setText(entry ? className(entry.classId) : v.kind in CLASSES ? className(v.kind as ClassId) : '');
      }
      const bossTag = this.scene.children.getByName(`boss-tag-${id}`) as Phaser.GameObjects.Text | undefined;
      if (bossTag) bossTag.setText(v.kind in ENEMIES ? enemyName(v.kind) : stageBossName());
    }
  }

  get spriteCount(): number {
    let n = 0;
    for (const v of this.views.values()) n += v.sprites.length;
    return n;
  }

  /** Cho công cụ kiểm chứng đọc tư thế xương thật của 1 đơn vị. */
  debugRig(unitId: number): { bones: Record<string, number>; root: { x: number; y: number; sx: number; sy: number } } | null {
    const v = this.views.get(unitId);
    if (!v) return null;
    const bones: Record<string, number> = {};
    for (const [name, bone] of v.rig.bones) bones[name] = bone.rot;
    const root = v.rig.bone('root');
    return { bones, root: { x: root.x, y: root.y, sx: root.sx, sy: root.sy } };
  }

  /** Xoá hết khi đổi màn. */
  reset(): void {
    for (const [, v] of this.views) {
      for (const s of v.sprites) s.destroy();
      v.shadow.destroy();
      v.hpBg.destroy();
      v.hpBar.destroy();
      v.manaBar.destroy();
      v.teamRing?.destroy();
      v.nameTag?.destroy();
    }
    this.views.clear();
  }
}

